import { useEffect, useRef, useState } from "react";

/**
 * Cloudflare Turnstile widget (explicit render). Loads the challenge script
 * on demand and reports the token (or null when expired/failed) via onToken.
 * Only rendered when a site key is configured — see config.turnstileSiteKey.
 *
 * Sizing: `size: "normal"` (300px) with the wrapper centering it. The
 * "flexible" size was tried but has a HARD 300px min-width per the docs —
 * it stalled entirely below that (no iframe, no token) in the 278px
 * feedback card, so the card was widened to 340px (300px content) instead.
 * The wrapper collapses once solved (`.turnstile-solved`) so no empty
 * 69px hole is left between the fields and the submit button.
 */
declare global {
  interface Window {
    turnstile?: {
      render(
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: string;
          size?: string;
        },
      ): string;
      reset(widgetId: string): void;
      remove(widgetId: string): void;
    };
  }
}

export function Turnstile({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  const [solved, setSolved] = useState(false);

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let widgetId: string | null = null;
    let cancelled = false;

    const render = () => {
      if (cancelled || !ref.current || !window.turnstile) return;
      try {
        widgetId = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            setSolved(true);
            onTokenRef.current(token);
          },
          "expired-callback": () => {
            setSolved(false);
            onTokenRef.current(null);
          },
          "error-callback": () => {
            setSolved(false);
            onTokenRef.current(null);
          },
          theme: "dark",
        });
      } catch {
        onTokenRef.current(null);
      }
    };

    if (window.turnstile) {
      render();
    } else {
      const script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = render;
      script.onerror = () => onTokenRef.current(null);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey]);

  return (
    <div
      ref={ref}
      className={`turnstile-widget${solved ? " turnstile-solved" : ""}`}
    />
  );
}
